import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import { InvoiceStanding } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceStanding";
import { InvoiceState } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceState";
import { InvoiceType } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceType";
import type { BadgeVariant } from "@freightsimple/ui-core/Display/Badge";
import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { ComponentType } from "react";
import { invoiceStatusDescription } from "@/Shared/Invoices/Helpers/invoiceUtils";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { PauseCircleIcon } from "@phosphor-icons/react/PauseCircle";
import { WarningCircleIcon } from "@phosphor-icons/react/WarningCircle";
import { XCircleIcon } from "@phosphor-icons/react/XCircle";

// Which Badge variant and glyph each standing gets. The label itself comes from
// invoiceStatusDescription, so this only decides colour + icon: failed and
// overdue both read as danger (their labels tell them apart), refunds and
// voided invoices are closed states in neutral dark.
function badgePresentation(invoice: Invoice): {
  variant: BadgeVariant;
  icon: ComponentType<{ className?: string }>;
} {
  const isRefund =
    invoice.invoiceType === InvoiceType.AdditionalChargeRefund ||
    invoice.invoiceType === InvoiceType.ShipmentRefund;

  switch (invoice.standing) {
    case InvoiceStanding.Paid:
      return {
        variant: isRefund ? "dark" : "success",
        icon: CheckCircleIcon,
      };
    case InvoiceStanding.PaymentSent:
      return { variant: "info", icon: PauseCircleIcon };
    case InvoiceStanding.Voided:
      return { variant: "dark", icon: XCircleIcon };
    // A dispute is a stop, not a warning that something is coming - hence the
    // X rather than the warning triangle the money states use.
    case InvoiceStanding.Disputed:
      return { variant: "danger", icon: XCircleIcon };
    case InvoiceStanding.Overdue:
      return { variant: "danger", icon: WarningCircleIcon };
    case InvoiceStanding.Open:
      return {
        variant: invoice.lastPaymentAttemptFailed ? "danger" : "warning",
        icon: WarningCircleIcon,
      };
  }
}

interface InvoiceStatusPillProps {
  invoice: Invoice;
}

function tooltipFor(invoice: Invoice) {
  if (invoice.invoiceState === InvoiceState.PaymentSent) {
    return (
      <Stack gap={1} align="start">
        <T size="sm">Bank account debits take a number of days to confirm.</T>
        <T size="sm">
          When the transfer is confirmed you will see the invoice as
          &apos;Paid&apos;.
        </T>
      </Stack>
    );
  }

  // Spelled out because it is the one status whose consequences a customer
  // can't guess, and not knowing them is what sent Darwynn looking for a
  // block that wasn't there.
  if (invoice.standing === InvoiceStanding.Disputed) {
    return (
      <Stack gap={1} align="start">
        <T size="sm">
          We&apos;re reviewing this invoice, so it&apos;s on hold. We won&apos;t
          charge it while it&apos;s in dispute.
        </T>
        <T size="sm">
          It isn&apos;t counted as overdue and it isn&apos;t holding up booking.
        </T>
      </Stack>
    );
  }

  return undefined;
}

/**
 * The invoice-standing badge on an invoice row. The standing is computed by the
 * server, so this component never compares a due date to today - see
 * invoiceStatusDescription. Paid splits two ways depending on whether the
 * invoice is a refund, and an open invoice reads as danger once a payment
 * attempt has failed. Payment-sent and disputed explain themselves in a
 * tooltip; the due date lives in its own table column, not here.
 */
export function InvoicesStatusPill({ invoice }: InvoiceStatusPillProps) {
  const { variant, icon } = badgePresentation(invoice);

  return (
    <Badge
      label={invoiceStatusDescription(invoice)}
      variant={variant}
      icon={icon}
      tooltip={tooltipFor(invoice)}
    />
  );
}
